
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { RATE_LIMIT_EVENT } from '../services/api';
import { detailMessage } from '../utils/errors';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType) => {
        // Defensive: callers occasionally pass `err.response?.data?.detail`
        // directly, which FastAPI may return as an object (e.g. trial-ended
        // 403 → {reason, workspace_id, ...}). Rendering that as JSX would
        // crash the tree. Coerce to a safe string before storing.
        const safeMessage = typeof message === 'string'
            ? message
            : detailMessage(message);
        const id = Math.random().toString(36).substr(2, 9);
        setToasts(prev => [...prev, { id, message: safeMessage, type }]);

        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    // Track whether a recent 429 was already shown to debounce rapid duplicates
    const lastRateLimitToast = useRef<number>(0);

    useEffect(() => {
        const handler = (e: Event) => {
            const now = Date.now();
            // Debounce: skip if a rate-limit toast was shown in the last 3 seconds
            if (now - lastRateLimitToast.current < 3000) return;
            lastRateLimitToast.current = now;

            const detail = (e as CustomEvent).detail;
            const seconds = detail?.retryAfterSeconds;
            const msg = seconds
                ? `Too many requests. Please wait ${seconds}s before trying again.`
                : 'Too many requests. Please wait before trying again.';
            showToast(msg, 'error');
        };
        window.addEventListener(RATE_LIMIT_EVENT, handler);
        return () => window.removeEventListener(RATE_LIMIT_EVENT, handler);
    }, [showToast]);

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`
              flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white min-w-[300px] animate-slide-up
              ${toast.type === 'success' ? 'bg-green-600' :
                                toast.type === 'error' ? 'bg-red-600' :
                                    'bg-blue-600'}
            `}
                    >
                        {toast.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0" />}
                        {toast.type === 'error' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                        {toast.type === 'info' && <Info className="w-5 h-5 flex-shrink-0" />}

                        <p className="text-sm font-medium flex-grow">{toast.message}</p>

                        <button
                            onClick={() => removeToast(toast.id)}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
